/**
 * Outils de dictée CIBLÉE dans l'éditeur V2, montés dans `BlockShell` :
 *  - sur une QUESTION (`statement_question`) : « Dicter la réponse » (remplace le
 *    corrigé de cette question, avec confirmation) + « Dicter une figure » ;
 *  - sur un CORRIGÉ (`correction`) : « Dicter une modification » (patch ciblé) +
 *    « Dicter une figure ».
 *
 * Réutilise `DictationCorrectionControl` (record → transcription → onStructure)
 * et les hooks de dictée ciblée. L'audio n'est jamais stocké.
 */

import { useState } from 'react';
import { Mic, Shapes, X } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import type { ExerciseBlock } from '@/lib/exercise-v2/types';
import { listQuestions, buildDeleteSubtreeOps } from '@/lib/exercise-v2/block-helpers';
import { useBlocksMutate } from '@/lib/exercise-v2/api';
import {
  useDictationPhase,
  useDictationEditCorrection,
  useDictateFigure,
} from '@/lib/exercise-v2/use-v2-generation';
import { DictationCorrectionControl } from '@/features/exercise-v2-wizard';
import {
  transcriptKey,
  getSavedTranscript,
  saveTranscript,
} from '@/features/exercise-v2-wizard/dictation-transcript-store';

export interface BlockVoiceToolsProps {
  exerciseId: string;
  block: ExerciseBlock;
  blocks: ExerciseBlock[];
}

type Tool = 'answer' | 'edit' | 'figure';

function errMessage(e: unknown): string | null {
  if (!e) return null;
  return e instanceof Error ? e.message : 'Erreur inattendue';
}

export function BlockVoiceTools({ exerciseId, block, blocks }: BlockVoiceToolsProps) {
  const [open, setOpen] = useState<Tool | null>(null);

  const dictation = useDictationPhase(exerciseId);
  const editCorrection = useDictationEditCorrection(exerciseId);
  const figure = useDictateFigure(exerciseId);
  const blocksMutate = useBlocksMutate(exerciseId);

  const isQuestion = block.block_type === 'statement_question';
  const isCorrection = block.block_type === 'correction';
  const isDistractor = block.block_type === 'distractor';
  if (!isQuestion && !isCorrection && !isDistractor) return null;

  const busy =
    dictation.isPending || editCorrection.isPending || figure.isPending || blocksMutate.isPending;

  // Index 0-based de la question (pour la dictée ciblée).
  const questionIndex = isQuestion ? listQuestions(blocks).findIndex((q) => q.id === block.id) : -1;

  // Clés de persistance de la dernière transcription (par cible × outil).
  const answerKey = transcriptKey(exerciseId, block.id, 'answer');
  const editKey = transcriptKey(exerciseId, block.id, 'edit');
  const figureKey = transcriptKey(exerciseId, block.id, 'figure');

  // Texte de départ éditable pour « Dicter la réponse » : la transcription brute
  // mémorisée si on a déjà dicté, SINON le corrigé actuel de la question (pour
  // pouvoir le relire/retoucher et re-corriger, même sans dictée antérieure).
  const corrigeText = isQuestion
    ? blocks
        .filter((b) => b.block_type === 'correction' && b.parent_id === block.id)
        .sort((a, b) => a.position - b.position)
        .map((c) => {
          const expl = typeof c.data.explanation_md === 'string' ? c.data.explanation_md : '';
          const ans = typeof c.data.answer_md === 'string' ? c.data.answer_md : '';
          return ans ? `${expl} Réponse : ${ans}` : expl;
        })
        .filter(Boolean)
        .join('\n')
    : '';
  const answerInitial = getSavedTranscript(answerKey) || corrigeText;
  // « Dicter une modification » = instruction orale (≠ contenu du bloc) → on ne
  // pré-remplit qu'avec la dernière instruction mémorisée.
  const editInitial = getSavedTranscript(editKey);

  async function handleDictateAnswer(transcript: string) {
    if (questionIndex < 0) return;
    const existing = blocks.filter(
      (b) => b.block_type === 'correction' && b.parent_id === block.id,
    );
    if (existing.length > 0) {
      const ok = window.confirm(
        'Remplacer le corrigé existant de cette question (et ses distracteurs/indices) par la dictée ?',
      );
      if (!ok) return;
      const ops = existing.flatMap((c) => buildDeleteSubtreeOps(blocks, c.id));
      if (ops.length > 0) await blocksMutate.mutateAsync(ops);
    }
    await dictation.mutateAsync({ transcript, questionIndex });
    saveTranscript(answerKey, transcript);
    setOpen(null);
  }

  async function handleEdit(transcript: string) {
    await editCorrection.mutateAsync({ correctionBlockId: block.id, transcript });
    saveTranscript(editKey, transcript);
    setOpen(null);
  }

  async function handleFigure(transcript: string) {
    await figure.mutateAsync({ targetBlockId: block.id, transcript });
    saveTranscript(figureKey, transcript);
    setOpen(null);
  }

  const figureTool = { id: 'figure' as const, label: 'Dicter une figure', Icon: Shapes };
  const tools: { id: Tool; label: string; Icon: typeof Mic }[] = isQuestion
    ? [{ id: 'answer', label: 'Dicter la réponse', Icon: Mic }, figureTool]
    : isCorrection
      ? [{ id: 'edit', label: 'Dicter une modification', Icon: Mic }, figureTool]
      : // distracteur : on peut seulement lui attacher une figure (ex. le graphe
        // d'une option « choisis le bon graphique »).
        [figureTool];

  return (
    <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50/60 p-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {tools.map((t) => {
          const Icon = t.Icon;
          const active = open === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setOpen(active ? null : t.id)}
              disabled={busy}
              aria-pressed={active}
              className={cn(
                'inline-flex min-h-11 items-center gap-1.5 rounded-lg px-2.5 font-sans text-[12px] font-semibold transition-colors active:scale-[0.98] disabled:opacity-50',
                active
                  ? 'bg-primary-500 text-white'
                  : 'text-ink-700 bg-white ring-1 ring-slate-200 hover:bg-slate-100',
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          );
        })}
        {open && (
          <button
            type="button"
            onClick={() => setOpen(null)}
            className="text-ink-400 ml-auto inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-slate-100"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {open === 'answer' && (
        <DictationCorrectionControl
          onStructure={handleDictateAnswer}
          structuring={dictation.isPending || blocksMutate.isPending}
          structureError={errMessage(dictation.error)}
          structureLabel="Remplacer le corrigé par la dictée"
          disabled={busy && !dictation.isPending}
          initialTranscript={answerInitial}
        />
      )}
      {open === 'edit' && (
        <DictationCorrectionControl
          onStructure={handleEdit}
          structuring={editCorrection.isPending}
          structureError={errMessage(editCorrection.error)}
          structureLabel="Appliquer la modification"
          disabled={busy && !editCorrection.isPending}
          initialTranscript={editInitial}
        />
      )}
      {open === 'figure' && (
        <DictationCorrectionControl
          onStructure={handleFigure}
          structuring={figure.isPending}
          structureError={errMessage(figure.error)}
          structureLabel="Générer la figure"
          disabled={busy && !figure.isPending}
          initialTranscript={getSavedTranscript(figureKey)}
        />
      )}
    </div>
  );
}
